import React, { useEffect, useState } from 'react';
import {
  CalendarDays,
  BellRing,
  MapPin,
  MessageCircle,
  Package,
  Phone,
  ShieldCheck,
  Store,
  UserRound,
  X,
} from 'lucide-react';
import { getDeliveryIntelligenceDetail } from './deliveryIntelligenceApi';
import StoreDeliverySignalModal from './StoreDeliverySignalModal';
import {
  AttentionBadge,
  COMMUNICATION_LABELS,
  formatContactDuration,
  REASON_LABELS,
  STAGE_LABELS,
  StoreWorkflowBadge,
} from './DeliveryIntelligenceCard';

export default function StoreDeliveryIntelligenceDrawer({ item: initialItem, date, onClose, onSignalReported }) {
  const [item, setItem] = useState(initialItem);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [showSignalModal, setShowSignalModal] = useState(false);

  useEffect(() => {
    let active = true;
    setItem(initialItem);
    setLoading(true);
    setError(false);

    getDeliveryIntelligenceDetail(initialItem.delivery_point_id, date ? { date } : {})
      .then(payload => {
        if (active && payload.item) setItem(payload.item);
      })
      .catch(() => {
        if (active) setError(true);
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [date, initialItem]);

  useEffect(() => {
    const onKeyDown = event => {
      if (event.key === 'Escape') {
        if (showSignalModal) setShowSignalModal(false);
        else onClose();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose, showSignalModal]);

  const delivery = item?.delivery || {};
  const communication = item?.communication || {};
  const recipient = item?.recipient_history || {};
  const storeHistory = item?.store_history || {};
  const attention = item?.attention || {};
  const workflow = item?.store_workflow;
  const duration = formatContactDuration(communication.minutes_since_outbound);

  return (
    <div className="fixed inset-0 z-[70] flex justify-end bg-slate-950/45 backdrop-blur-sm" role="dialog" aria-modal="true" aria-label="Detalle de inteligencia de entrega">
      <button type="button" className="absolute inset-0 cursor-default" onClick={onClose} aria-label="Cerrar detalle" />
      <aside className="relative flex h-full w-full max-w-xl flex-col bg-white shadow-2xl">
        <header className="flex items-start justify-between gap-3 border-b border-slate-200 px-5 py-4 sm:px-6">
          <div>
            <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-[#B1336E] font-montserrat">
              Historial con tu tienda
            </p>
            <h2 className="mt-1 text-xl font-extrabold text-slate-900 font-montserrat">
              Entrega #{item?.delivery_point_id}
            </h2>
            <p className="mt-1 text-xs text-slate-500 font-montserrat">
              Contexto operativo de tu propia cuenta
            </p>
          </div>
          <button type="button" onClick={onClose} className="rounded-xl p-2 text-slate-500 hover:bg-slate-100" aria-label="Cerrar">
            <X className="h-5 w-5" />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-5 py-5 sm:px-6">
          {loading && (
            <div className="mb-4 h-1 overflow-hidden rounded-full bg-slate-100">
              <div className="h-full w-1/2 animate-pulse rounded-full bg-[#B1336E]" />
            </div>
          )}
          {error && (
            <p className="mb-4 rounded-xl border border-slate-200 bg-slate-50 p-3 text-xs text-slate-500 font-montserrat">
              No se pudo actualizar el detalle. Mostramos el contexto ya cargado.
            </p>
          )}

          <div className="flex flex-wrap items-center gap-2">
            <AttentionBadge level={attention.level} />
            <StoreWorkflowBadge workflow={workflow} />
            <span className="rounded-full border border-[#B1336E]/25 bg-[#B1336E]/10 px-2.5 py-1 text-[11px] font-extrabold uppercase tracking-wide text-[#B1336E] font-montserrat">
              {STAGE_LABELS[item?.stage] || item?.stage}
            </span>
          </div>

          <section className="mt-5 rounded-2xl border border-slate-200 p-4">
            <h3 className="flex items-center gap-2 text-sm font-extrabold text-slate-900 font-montserrat">
              <UserRound className="h-4 w-4 text-[#B1336E]" /> Entrega actual
            </h3>
            <div className="mt-3 space-y-2 text-sm text-slate-700 font-montserrat">
              <p className="font-bold text-slate-900">{delivery.recipient_name}</p>
              <p className="flex items-center gap-2"><Phone className="h-4 w-4 text-slate-400" /> {delivery.recipient_phone}</p>
              <p className="flex items-start gap-2"><MapPin className="mt-0.5 h-4 w-4 flex-shrink-0 text-slate-400" /> {delivery.address || 'Sin dirección visible'}{delivery.district ? ` · ${delivery.district}` : ''}</p>
              <p className="flex items-center gap-2"><CalendarDays className="h-4 w-4 text-slate-400" /> {delivery.delivery_date ? new Date(delivery.delivery_date).toLocaleDateString('es-PE', { timeZone: 'America/Lima' }) : 'Sin fecha'}</p>
              {delivery.product && <p className="flex items-start gap-2"><Package className="mt-0.5 h-4 w-4 flex-shrink-0 text-slate-400" /> {delivery.product}</p>}
            </div>
          </section>

          {workflow && (
            <section className="mt-4 rounded-2xl border border-violet-200 bg-violet-50 p-4">
              <h3 className="flex items-center gap-2 text-sm font-extrabold text-violet-900 font-montserrat">
                <BellRing className="h-4 w-4" /> Reporte enviado a Fast Max
              </h3>
              <p className="mt-2 text-xs leading-5 text-violet-800 font-montserrat">
                Estado: <strong>{workflow.state}</strong>
                {workflow.reported_at ? ` · Enviado ${new Date(workflow.reported_at).toLocaleString('es-PE', { timeZone: 'America/Lima' })}` : ''}
              </p>
              {workflow.note && <p className="mt-2 text-xs text-violet-800 font-montserrat">“{workflow.note}”</p>}
            </section>
          )}

          <section className="mt-4 rounded-2xl border border-slate-200 p-4">
            <h3 className="flex items-center gap-2 text-sm font-extrabold text-slate-900 font-montserrat">
              <MessageCircle className="h-4 w-4 text-[#B1336E]" /> Comunicación visible
            </h3>
            <p className="mt-3 text-sm font-bold text-slate-800 font-montserrat">
              {COMMUNICATION_LABELS[communication.state] || communication.state}
              {duration ? ` · ${duration}` : ''}
            </p>
            <p className="mt-1 text-xs leading-5 text-slate-500 font-montserrat">
              “Cliente respondió” solo indica que existe una respuesta; no significa que la entrega esté confirmada.
            </p>
          </section>

          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <section className="rounded-2xl border border-slate-200 p-4">
              <h3 className="flex items-center gap-2 text-sm font-extrabold text-slate-900 font-montserrat">
                <ShieldCheck className="h-4 w-4 text-[#B1336E]" /> Destinatario contigo
              </h3>
              <dl className="mt-3 space-y-2 text-xs font-montserrat">
                <div className="flex justify-between"><dt className="text-slate-500">Entregadas</dt><dd className="font-bold text-slate-900">{recipient.delivered || 0}</dd></div>
                <div className="flex justify-between"><dt className="text-slate-500">Canceladas</dt><dd className="font-bold text-red-700">{recipient.cancelled || 0}</dd></div>
                <div className="flex justify-between"><dt className="text-slate-500">Rechazadas</dt><dd className="font-bold text-slate-900">{recipient.rejected || 0}</dd></div>
              </dl>
            </section>
            <section className="rounded-2xl border border-slate-200 p-4">
              <h3 className="flex items-center gap-2 text-sm font-extrabold text-slate-900 font-montserrat">
                <Store className="h-4 w-4 text-[#B1336E]" /> Tu cuenta · 90 días
              </h3>
              <p className="mt-3 text-2xl font-extrabold text-slate-900 font-montserrat">
                {Number(storeHistory.cancellation_rate_pct_90d || 0).toFixed(1)}%
              </p>
              <p className="text-xs text-slate-500 font-montserrat">cancelación · {storeHistory.terminal_attempts_90d || 0} intentos</p>
              <p className="mt-2 text-[11px] font-bold uppercase text-slate-400 font-montserrat">Muestra {storeHistory.confidence === 'ADEQUATE' ? 'adecuada' : 'baja'}</p>
            </section>
          </div>

          <section className="mt-4 rounded-2xl border border-[#B1336E]/20 bg-[#B1336E]/5 p-4">
            <h3 className="text-sm font-extrabold text-slate-900 font-montserrat">Por qué aparece</h3>
            <div className="mt-3 flex flex-wrap gap-2">
              {(attention.reason_codes || []).map(code => (
                <span key={code} className="rounded-full border border-white bg-white px-2.5 py-1 text-[11px] font-bold text-slate-600 shadow-sm font-montserrat">
                  {REASON_LABELS[code] || code.split('_').join(' ')}
                </span>
              ))}
            </div>
            <p className="mt-4 text-sm font-bold leading-6 text-[#8f2859] font-montserrat">
              {attention.recommendation}
            </p>
          </section>

          <p className="mt-5 text-xs leading-5 text-slate-500 font-montserrat">
            Esta inteligencia es explicable y orientativa. No cancela, posterga, bloquea ni modifica la entrega automáticamente.
          </p>
        </div>

        <footer className="border-t border-slate-200 bg-white px-5 py-4 sm:px-6">
          <div className="flex flex-col-reverse gap-2 sm:flex-row">
            <button type="button" onClick={onClose} className="flex-1 rounded-xl bg-slate-100 px-4 py-3 text-sm font-bold text-slate-700 hover:bg-slate-200 font-montserrat">
              Cerrar
            </button>
            {item?.is_active !== false && (
              <button type="button" onClick={() => setShowSignalModal(true)} className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-[#B1336E] px-4 py-3 text-sm font-bold text-white hover:bg-[#9c2d61] font-montserrat">
                <BellRing className="h-4 w-4" /> Reportar novedad
              </button>
            )}
          </div>
        </footer>
      </aside>

      {showSignalModal && (
        <StoreDeliverySignalModal
          item={item}
          onClose={() => setShowSignalModal(false)}
          onCompleted={async result => {
            if (result?.signal) {
              setItem(current => ({
                ...current,
                store_workflow: result.signal,
                suppress_from_attention: Boolean(result.signal.suppress_from_attention),
              }));
            }
            setShowSignalModal(false);
            await onSignalReported?.(result);
          }}
        />
      )}
    </div>
  );
}
